import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { appInsightsService } from "./services/appInsights.service";
import { routeTemplate, startWebVitals } from "./services/webVitals";
import { globalErrorHandler } from "./services/globalErrorHandler.service";
import "./css/chromeAutofill.css";
import { useSessionConfig } from "./hooks/useSessionConfig";
import { useAppInitialization } from "./hooks/useAppInitialization";
import { useTenantCreation } from "./hooks/useTenantCreation";
import { useMsalAuth } from "./hooks/useMsalAuth";
import { loadUserPreferences } from "./services/userPreferencesService";
import useWindowDimensions from "./common/useWindowDimensions";
import { HeaderBanner } from "./components/HeaderBanner";
import SessionTimeout from "./components/shared/SessionTimeout";
import ErrorPage from "./components/shared/errorpage";
import { AccessRefusalDialog } from "./components/shared/AccessRefusalDialog";
import { refusalFor } from "./components/shared/accessRefusal";
import { AppLayout } from "./components/AppLayout";
import { AppLoadingStates } from "./components/app/AppLoadingStates";
import { NoAccessScreen } from "./components/access/NoAccessScreen";
import { useAccessDenial } from "./components/access/accessDenial";
import { IRootReducerState } from "./store/IRootReducer";
import { setIsError } from "./store/slices/authSlice";
import { ThemeProvider } from "./contexts/ThemeContext";

const reactPlugin = appInsightsService.initialize();

// Real-user Core Web Vitals, one row per metric per page load. See webVitals.ts.
startWebVitals(
  (name, value, properties) =>
    appInsightsService.trackMetric(`WebVitals_${name}`, value, properties),
  () => appInsightsService.flush(),
);

// Initialize global error handlers
globalErrorHandler.initialize();

// Performance timing for login-to-landing measurement
const LOGIN_PERF_KEY = "__SKY_LOGIN_PERF__";

function AppNew() {
  const { currentUser, getQueryString, getAccessToken } = useMsalAuth();

  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const dispatch = useDispatch();
  const { isError, errorMessage, errorCode } = authState.error;
  // A 403 is a refused action, not a broken session, so it shows over the app
  // instead of replacing it (H6). Only a coded error is a refusal; a bare
  // setIsError(true) still gets the old page.
  const refusal = isError && refusalFor(errorCode) ? errorCode : null;
  const blockingError = isError && !refusal;
  const isFullAccess = authState.isFullAccess;
  // Deny-by-default (Phase U): once the server says this user can't use the
  // instance, the no-access screen replaces the whole app, header and sidebar too.
  const denial = useAccessDenial();

  // Eagerly acquire MSAL token and sync to Redux so RTK Query
  // has a valid Authorization header before page components mount
  useEffect(() => {
    if (!authState.accessToken) {
      getAccessToken();
    }
  }, [authState.accessToken, getAccessToken]);

  // Start performance timing on mount
  useEffect(() => {
    if (!window[LOGIN_PERF_KEY]) {
      window[LOGIN_PERF_KEY] = {
        startTime: performance.now(),
        appMounted: Date.now(),
      };
    }
  }, []);

  const { sessionDefault, setSessionDefault, isSessionDefaultLoaded, fetchSessionConfig } =
    useSessionConfig();

  const {
    isAppLoaded,
    isCheckResourceFailed,
    isShowCreateTenant,
    createOwnInfrastructure,
    showExternalTenantCreationError,
    instanceId,
    userAccesstoInstance,
    setInstanceId,
    checkIfResourceExist,
    hasAccessToInstance,
    getUserAccess,
    hideExternalTenantCreationError,
    setIsShowCreateTenant,
    setCreateOwnInfrastructure,
    setShowExternalTenantCreationError,
    setIsCheckResourceFailed,
  } = useAppInitialization({
    currentUserId: currentUser?.id,
    getQueryString,
    fetchSessionConfig,
  });

  const [isSetupFinished, setIsSetupFinished] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const { createResourceForTenant, createOwnInfrastructureFn } = useTenantCreation({
    instanceId,
    getQueryString,
    setIsShowCreateTenant,
    setCreateOwnInfrastructure,
    setShowExternalTenantCreationError,
    setIsCheckResourceFailed,
    setNotificationMessage,
    setIsSetupFinished,
    getUserAccess,
    checkIfResourceExist,
  });

  const { windowWidth } = useWindowDimensions();

  useEffect(() => {
    document.body.classList.remove("hold-transition");
  }, []);

  // Log performance when app finishes loading
  useEffect(() => {
    const perf = window[LOGIN_PERF_KEY];
    if (isAppLoaded && perf) {
      const loadTime = performance.now() - perf.startTime;
      // Sign-in-to-usable journey time, from app mount and from navigation
      // start, so the login redirect is visible on its own.
      appInsightsService.trackMetric("Journey_AppReady", loadTime, {
        route: routeTemplate(window.location.pathname),
      });
      appInsightsService.trackMetric("Journey_AppReadyFromNavigation", performance.now(), {
        route: routeTemplate(window.location.pathname),
      });
      // Clear to avoid duplicate logs
      delete window[LOGIN_PERF_KEY];
    }
  }, [isAppLoaded]);

  useEffect(() => {
    const initializeApp = async () => {
      // Get URL instanceId first
      const params = new URLSearchParams(window.location.search);
      const urlInstanceId = params.get("instanceid") || "";

      if (urlInstanceId) {
        // URL has instanceId - use it directly (active session navigation)
        setInstanceId(urlInstanceId);
        hasAccessToInstance(urlInstanceId);
        return;
      }

      // No URL instanceId - load preferences (localStorage for active session, DB for fresh login)
      const preferences = await loadUserPreferences();
      const savedInstanceId = preferences?.instanceId || null;

      if (savedInstanceId) {
        // Update URL with saved instanceId (without full page reload)
        const pathname = window.location.pathname || "/";
        const newUrl = pathname + "?instanceid=" + savedInstanceId;
        window.history.replaceState({}, "", newUrl);
        setInstanceId(savedInstanceId);
        hasAccessToInstance(savedInstanceId, { remembered: true });
      } else {
        // No instanceId anywhere - check if resource exists
        checkIfResourceExist();
      }
    };

    initializeApp();
  }, []);

  const renderAppContent = () => {
    if (denial) {
      return (
        <NoAccessScreen
          code={denial.code}
          instanceId={denial.instanceId}
        />
      );
    }

    if (!isAppLoaded) {
      return (
        <AppLoadingStates
          userAccesstoInstance={userAccesstoInstance}
          showExternalTenantCreationError={showExternalTenantCreationError}
          isCheckResourceFailed={isCheckResourceFailed}
          isShowCreateTenant={isShowCreateTenant}
          createOwnInfrastructure={createOwnInfrastructure}
          isSetupFinished={isSetupFinished}
          hideExternalTenantCreationError={hideExternalTenantCreationError}
          createResourceForTenant={createResourceForTenant}
          createOwnInfrastructureFn={createOwnInfrastructureFn}
        />
      );
    }

    if (blockingError) {
      return (
        <ErrorPage
          show={true}
          message={errorMessage}
          errorCode={errorCode}
          title="Warning!"
        />
      );
    }

    return (
      <AppLayout
        isFullAccess={isFullAccess}
        windowWidth={windowWidth}
        notificationMessage={notificationMessage}
        setSessionDefault={setSessionDefault}
      />
    );
  };

  return (
    <ThemeProvider>
      {isAppLoaded && !blockingError && !denial && <HeaderBanner />}
      {isSessionDefaultLoaded && (
        <SessionTimeout
          isInactivityActive={sessionDefault.isInactivityActive}
          inactivityLength={sessionDefault.inactivityLength}
          inactivityTimeout={sessionDefault.inactivityTimeout}
          isSessionExpiryActive={sessionDefault.isSessionExpiryActive}
          sessionLength={sessionDefault.sessionLength}
          sessionTimeout={sessionDefault.sessionTimeout}
        />
      )}
      {renderAppContent()}
      {refusal && (
        <AccessRefusalDialog
          code={refusal}
          onClose={() => dispatch(setIsError(false))}
        />
      )}
    </ThemeProvider>
  );
}

export default withAITracking(reactPlugin, AppNew);
